import { useCallback, useState, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { useOpen } from '../components/search-result-listing/header-section/searchbar/hooks'
import AnalyticsService from '../services/analytics-service'
import { commonPageEvents } from '../services/analytics-service/events'
import RegionsAutocompleteSuggestionTransport from '../transports/regions-autocomplete-suggestion-transport'
import { SearchbarHistoryItem } from '../redux/searchbar/types'
import { useDateFormatter } from '../hooks/date'
import { getFilterFromHref } from '../utils/request-utils'
import { getSearchDate } from '../utils/miscellaneous-utils'
import { useSearchUrl } from './search'
import { scheduleIdleCallback } from '../utils/browser-utils'

interface UseControlledSearchbarLogicProps {
    location: { search: string; regionId: number | null }
    arrivalDate: Date | null
    departureDate: Date | null
    adultsCount: number
    childrenCount: number

    onLocationChange: (loc: { search: string; regionId: number | null }) => void
    onArrivalDateChange: (date: Date | null) => void
    onDepartureDateChange: (date: Date | null) => void
    onAdultsCountChange: (count: number) => void
    onChildrenCountChange: (count: number) => void
}

export function useControlledSearchbarLogic({
    location,
    arrivalDate,
    departureDate,
    adultsCount,
    childrenCount,
    onLocationChange,
    onArrivalDateChange,
    onDepartureDateChange,
    onAdultsCountChange,
    onChildrenCountChange,
}: UseControlledSearchbarLogicProps) {
    const {
        isOpen: isLocationPickerOpen,
        onOpen: onLocationPickerOpen,
        onClose: onLocationPickerClose,
    } = useOpen(false)

    const { isOpen: isDatePickerOpen, onOpen: onDatePickerOpen, onClose: onDatePickerClose } = useOpen(false)

    const { isOpen: isGuestPickerOpen, onOpen: onGuestPickerOpen, onClose: onGuestPickerClose } = useOpen(false)

    const [locationPickerError, setLocationPickerError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { format } = useDateFormatter()
    const { getSearchUrl } = useSearchUrl()

    // Local state for instant input responsiveness
    const [localSearch, setLocalSearch] = useState(location.search)

    // Debounced parent state update
    const debouncedOnLocationChange = useMemo(
        () =>
            debounce((input: string, currentLocation: typeof location) => {
                onLocationChange(currentLocation.search !== input ? { regionId: null, search: input } : currentLocation)
            }, 150),
        [onLocationChange],
    )

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedOnLocationChange.cancel()
        }
    }, [debouncedOnLocationChange])

    // Sync local state when parent location changes externally (e.g., history item select)
    useEffect(() => {
        setLocalSearch(location.search)
    }, [location.search])

    // -------------------------
    // Location Handlers
    // -------------------------

    const handleLocationPickerOpen = useCallback(() => {
        setLocationPickerError('')
        scheduleIdleCallback(() => {
            onLocationPickerOpen()
        })
    }, [onLocationPickerOpen])

    const handleLocationInputChange = useCallback(
        (input: string) => {
            // Update local state immediately for responsive UI
            setLocalSearch(input)

            // Debounce parent state update to reduce re-renders
            debouncedOnLocationChange(input, location)
        },
        [location, debouncedOnLocationChange],
    )

    const handleLocationInputSelect = useCallback(
        (value: string) => {
            handleLocationInputChange(value)
            onLocationPickerClose()
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [handleLocationInputChange, arrivalDate, departureDate, onLocationPickerClose, onDatePickerOpen],
    )

    const handleLocationItemSelect = useCallback(
        (item: RegionsAutocompleteSuggestionTransport) => {
            onLocationChange({ search: item.title, regionId: item.regionId })
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [arrivalDate, departureDate, onLocationChange, onDatePickerOpen],
    )

    // -------------------------
    // History Select
    // -------------------------

    const handleHistoryItemSelect = useCallback(
        async (history: SearchbarHistoryItem) => {
            const { search, arrival, departure, adults, children } = history

            // Set loading state for visual feedback
            setIsSubmitting(true)

            onLocationChange({ regionId: null, search })
            onArrivalDateChange(arrival)
            onDepartureDateChange(departure)
            onAdultsCountChange(adults)
            onChildrenCountChange(children)

            try {
                const searchUrl = await getSearchUrl(getFilterFromHref(window.location.href), {
                    regionId: location.regionId,
                    search,
                    arrival: getSearchDate(arrival),
                    departure: getSearchDate(departure),
                    adults,
                    children,
                })

                setIsSubmitting(false)
                window.location.href = `${window.location.origin}${searchUrl}`
            } catch (error) {
                setIsSubmitting(false)
                console.error('Search URL generation failed:', error)
            }
        },
        [
            onLocationChange,
            onArrivalDateChange,
            onDepartureDateChange,
            onAdultsCountChange,
            onChildrenCountChange,
            location.regionId,
            getSearchUrl,
        ],
    )

    // -------------------------
    // Date Tracking
    // -------------------------

    const trackArrivalDateChange = useCallback(
        (startDate: Date | null, endDate?: Date | null) => {
            if (!startDate) {
                return
            }

            const formattedStartDateDot = format(startDate, 'dd.MM.yyyy')
            const formattedStartDateDash = format(startDate, 'yyyy-MM-dd')

            AnalyticsService.trackEvent({
                event: 'datepicker_select',
                eventScope: 'dates[form_date_from]',
                eventValue: formattedStartDateDot,
            })

            AnalyticsService.trackEvent({
                action: 'searchbox_add_arrival',
                category: 'searchbox',
                label: formattedStartDateDash,
            })

            if (endDate) {
                AnalyticsService.trackEvent({
                    eventAction: 'entered_arrival_and_departure',
                    eventLabel: 'tbd',
                })
            }
        },
        [format],
    )

    const trackDepartureDateChange = useCallback(
        (startDate: Date | null, endDate: Date | null) => {
            if (!endDate) {
                return
            }

            const formattedEndDateDot = format(endDate, 'dd.MM.yyyy')
            const formattedEndDateDash = format(endDate, 'yyyy-MM-dd')

            AnalyticsService.trackEvent({
                event: 'datepicker_select',
                eventScope: 'dates[form_date_to]',
                eventValue: formattedEndDateDot,
            })

            AnalyticsService.trackEvent({
                action: 'searchbox_add_departure',
                category: 'searchbox',
                label: formattedEndDateDash,
            })

            if (startDate) {
                AnalyticsService.trackEvent({
                    eventAction: 'entered_arrival_and_departure',
                    eventLabel: 'tbd',
                })
            }
        },
        [format],
    )

    const handleDepartureDateChange = useCallback(
        (date: Date | null) => {
            onDepartureDateChange(date)
            trackDepartureDateChange(arrivalDate, date)

            if (date) {
                onDatePickerClose()
            }
        },
        [arrivalDate, onDepartureDateChange, trackDepartureDateChange, onDatePickerClose],
    )

    // -------------------------
    // Guest Picker
    // -------------------------

    const handleGuestPickerOpen = useCallback(() => {
        if (!isGuestPickerOpen) {
            AnalyticsService.trackEvent({
                event: commonPageEvents.searchBarSection.CLICK_PERS_FILTER,
                eventLabel: 'searchbar',
            })
        }
        onGuestPickerOpen()
    }, [isGuestPickerOpen, onGuestPickerOpen])

    const handleGuestPickerClose = useCallback(() => {
        if (isGuestPickerOpen) {
            AnalyticsService.trackEvent({
                event: commonPageEvents.searchBarSection.SENT_PERS_FILTER,
                eventLabel: 'searchbar',
                eventValue: `adults=${adultsCount} kids=${childrenCount}`,
            })
        }
        onGuestPickerClose()
    }, [isGuestPickerOpen, adultsCount, childrenCount, onGuestPickerClose])

    return {
        isLocationPickerOpen,
        isDatePickerOpen,
        isGuestPickerOpen,
        onLocationPickerClose,
        onDatePickerClose,
        onGuestPickerClose,
        onLocationPickerOpen,
        onDatePickerOpen,
        onGuestPickerOpen,

        locationPickerError,
        setLocationPickerError,

        isSubmitting,
        setIsSubmitting,

        // Local search for instant UI responsiveness
        localSearch,

        handleLocationPickerOpen,
        handleLocationInputChange,
        handleLocationInputSelect,
        handleLocationItemSelect,
        handleHistoryItemSelect,

        handleGuestPickerOpen,
        handleGuestPickerClose,

        trackArrivalDateChange,
        trackDepartureDateChange,
        handleDepartureDateChange,
    }
}
